import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { tradeTermsApi, F117 } from '../../../services/tradeTermsApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useTradeTermsForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F117[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [isOld, setIsOld] = useState(false);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try { const res = await tradeTermsApi.list(1, 1000); setRecords(res.data || []); return res.data || []; }
    catch { message.error('載入失敗'); return []; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') { setCode(current.f117_01); setName(current.f117_11); setIsOld(current.f117_12); }
  }, [current, mode]);

  useEffect(() => { loadRecords().then(d => { if (d.length > 0) setCurrentIdx(0); }); }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };
  const startAdd = () => { setMode('add'); setCode(''); setName(''); setIsOld(false); };
  const startEdit = () => { if (!current) return; setMode('edit'); setCode(current.f117_01); setName(current.f117_11); setIsOld(current.f117_12); };
  const cancel = () => { setMode('browse'); if (current) { setCode(current.f117_01); setName(current.f117_11); setIsOld(current.f117_12); } };

  const save = async () => {
    try {
      if (mode === 'add') { await tradeTermsApi.create({ f117_01: code, f117_11: name, f117_12: isOld }); message.success('新增成功'); }
      else { await tradeTermsApi.update(current!.f117_01, { f117_11: name, f117_12: isOld }); message.success('修改成功'); }
      const data = await loadRecords();
      const t = mode === 'add' ? code : current!.f117_01;
      const idx = data.findIndex(r => r.f117_01 === t);
      setCurrentIdx(idx >= 0 ? idx : 0); setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '儲存失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try { await tradeTermsApi.delete(current.f117_01); message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1)); else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (t: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f117_01 >= t);
    if (idx >= 0) {
      setCurrentIdx(idx);
    } else {
      message.warning('查無此代號');
    }
  };

  return { mode, isEditing, loading, records, current, currentIdx, code, setCode, name, setName, isOld, setIsOld,
    navFirst, navPrev, navNext, navLast, startAdd, startEdit, cancel, save, doDelete, quickSearch };
}
